import { Injectable } from '@angular/core';
import { HttpDataService, IHttpDataCache } from '../../../../shared/http/http-data.service';

import { ICodeValue, ICompany, ICompanyCountry, ICompanyCountryExtended, ICompanyExtended, IContact, ICountry, ICountryExtended, ICurrency, IDescribedObject, IDescribedValueObject, ILocationExtended, IRecord, IValueObject } from '../../../../../type_definitions/datatypes';
import { IRoleContact } from '../../../webshop/info/webshop-info-data.service';
import * as _ from 'underscore';
import { util } from '../../../../shared/util';
import { TranslationService } from '../../../../shared/translation/translation.service';

export interface ICompanyContact extends IRecord {
  contact:IContact;
  country:ICountryExtended;
  contactCompany:ICompanyExtended;
  contactType:{
    code:string;
    companyType:string;
    description:string;
  };
}

export interface IOEMDistributor extends IRecord {
  preferred:boolean;
  oft:number;
  companyCountry:ICompanyCountryExtended;
  shippingProcess:ICodeValue;
  poPlacement:ICodeValue;
  contacts:IRoleContact[];
  distributorIdWithVendor:string;
  paymentCurrency?:ICurrency;
}

export interface IOEMRelation extends IRecord {
  comments?:string;
  oem:ICompany;
  oft?:number;
  directOrder?:boolean;
  contacts?:ICompanyContact[];
  hasActiveWebshop?:boolean;
  basicInfoMissing?:boolean;
  tierStatus:IValueObject;
  ranking?:ICodeValue;
  directOrderProcess?:ICodeValue;
  paymentCurrency?:ICurrency;
  poPlacement?:ICodeValue;
  shippingProcess?:ICodeValue;
  distributors:IOEMDistributor[];
}

export interface IOEMRelationExtended extends IOEMRelation {
  distributor:ICompanyCountryExtended;
  availableDirectOrderModes?:IDescribedValueObject[];
}

export interface IOEMDetail extends IOEMRelationExtended {
  companyCountry:ICompanyCountryExtended;
  showDirectOrder?:boolean;
  showVendorValidator?:boolean;
  agreesToClipLevel?:boolean;
  percentageClipLevel?:number;
  shipToLocation?:ILocationExtended;
  shipToContact?:IContact;
  invoiceToLocation?:ILocationExtended;
  invoiceToContact?:IContact;
}

interface IFulfillmentSLAValue extends IDescribedObject {
  days?:number;
  sequence?:number;
}

export interface IOEMOrderFulfillmentTime {
  oft?:number;
  fulfillmentSlaValues?:IFulfillmentSLAValue[];
}

export interface ICLF {
  id:number;
  country:ICountry;
  vendor:ICompany;
  currency:string;
  importDuty:number;
  freight:number;
  brokerFee:number;
  insurance:number;
  distributorMargin:number;
  otherLocalUplift:number;
  fixAmount:number;
  vendorClf:number;
  otherUpliftDescription:string;
  category:string;
  comments:string;
  status:string;
}

export interface IOEMFilter {
  label:string;
  code:OEMFilterCode;
}

export interface IOEMTierStatusOption {
  labelClass:string;
  label:string;
  longLabel:string
}

export enum OEMFilterCode {
  ACTIVE = 'ACTIVE',
  INFO_MISSING = 'INFO_MISSING',
  COVERED = 'COVERED',
  NON_COVERED = 'NON_COVERED'
}

export enum OEMTierStatus {
  DIRECT = '0',
  DISTRIBUTOR = '2',
  NOT_COVERED = '3',
  UNKNOWN = '1'
}

@Injectable()
export class BPProfilesOEMDataService {
  private cache:IHttpDataCache<any>;

  constructor(
    private httpDataService:HttpDataService,
    private translationService:TranslationService
  ) {
    this.cache = httpDataService.createCache();
  }

  public getOEMFilters():IOEMFilter[] {
    return [
      {
        label: this.translationService.instant('BPPROFILES.OEM.FILTERS.ACTIVE'),
        code: OEMFilterCode.ACTIVE
      },
      {
        label: this.translationService.instant('BPPROFILES.OEM.FILTERS.INFO_MISSING'),
        code: OEMFilterCode.INFO_MISSING
      },
      {
        label: this.translationService.instant('BPPROFILES.OEM.FILTERS.COVERED'),
        code: OEMFilterCode.COVERED
      },
      {
        label: this.translationService.instant('BPPROFILES.OEM.FILTERS.NON_COVERED'),
        code: OEMFilterCode.NON_COVERED
      }
    ];
  }

  public getTierStatusOption(tierStatus:IValueObject):IOEMTierStatusOption {
    if (!tierStatus) {
      return;
    }

    switch (tierStatus.id) {
      case OEMTierStatus.DIRECT:
        return {
          labelClass: 'bg-success',
          label: 'Direct',
          longLabel: 'I buy directly from OEM'
        };
      case OEMTierStatus.DISTRIBUTOR:
        return {
          labelClass: 'bg-primary',
          label: 'Distributor',
          longLabel: 'I buy from DISTRIBUTOR'
        };
      case OEMTierStatus.NOT_COVERED:
        return {
          labelClass: 'bg-danger',
          label: 'Not covered',
          longLabel: 'I cannot buy from this OEM'
        };
    }
  }

  public getClipLevelOptions():IValueObject[] {
    return [
      { id: true, value: this.translationService.instant('BPPROFILES.OEM.AGREE_CLIP_LEVEL') },
      { id: false, value: this.translationService.instant('BPPROFILES.OEM.DISAGREE_CLIP_LEVEL') }
    ];
  }

  public getOEMRelations(profileId:number):Promise<IOEMRelation[]> {
    return this.httpDataService.doGET(
      `rest/bpprofiles/profiles/${profileId}/oemrelations`,
      undefined,
      undefined,
      'Error loading OEMs'
    );
  }

  public getOEMDetails(profileId:number, oemId:number, distributorId?:number):Promise<IOEMDetail> {
    let url:string = `rest/bpprofiles/profiles/${profileId}/oemrelations/${oemId}`;
    if (distributorId) {
      url += `/distributorrelations/${distributorId}`;
    }

    return this.httpDataService.doGET(
      url,
      undefined,
      undefined,
      `Error loading ${distributorId ? 'Distributor' : 'OEM'} details`
    );
  }

  public getBPLocations(profileId:number, onlyShipping?:boolean):Promise<ILocationExtended[]> {
    return this.httpDataService.doGET(
      `rest/bpprofiles/profiles/${profileId}/${onlyShipping? 'shippinglocations' : 'locations'}`,
      undefined,
      undefined,
      'Error loading BP locations'
    );
  }

  public getCountryDistributors(excludeDistributors?:IOEMDistributor[] | ICompanyCountryExtended[]):Promise<IOEMDistributor[]> {
    return this.cache.doGET(
      `rest/bpprofiles/countrydistributors`,
      undefined,
      undefined,
      'Error getting distributors'
    ).then((distributors:IOEMDistributor[]) => {
      if (excludeDistributors) {
        const excludeDistributorIds:number[] = util.selectUniqueProperties(excludeDistributors, 'companyCountry.id');
        return _.filter(distributors,
          (companyCountry:any) => {
            return !_.contains(excludeDistributorIds, companyCountry.id);
          });
      }
      return distributors;
    });
  }

  public addOEMRelation(profileId:number, oem:IOEMRelation|IOEMDetail):Promise<IOEMRelation|IOEMDetail> {
    return this.httpDataService.doPOST(
      `rest/bpprofiles/profiles/${profileId}/oemrelations`,
      undefined,
      oem,
      undefined,
      'Error adding OEM relation'
    );
  }

  public updateOEMRelation(profileId:number, oem:IOEMRelation|IOEMDetail):Promise<IOEMRelation|IOEMDetail> {
    return this.httpDataService.doPUT(
      `rest/bpprofiles/profiles/${profileId}/oemrelations/${oem.id}`,
      undefined,
      oem,
      undefined,
      'Error updating OEM relation'
    );
  }

  public updateOEMRelations(profileId:number, oemRelations:IOEMRelationExtended[]):Promise<IOEMRelation[]> {
    return this.httpDataService.doPUT(
      `rest/bpprofiles/profiles/${profileId}/oemrelations`,
      undefined,
      oemRelations,
      'Saving OEM relations',
      'Error updating OEM relations'
    );
  }

  public addNewDistributor(profileId:number, oemId:number, preferred:boolean, distributor:IOEMDistributor|ICompanyCountry):Promise<IOEMDistributor> {
    let url:string = `rest/bpprofiles/profiles/${profileId}/oemrelations/${oemId}/distributorrelations`;
    if (preferred) {
      url += `?preferred=${preferred}`;
    }

    return this.httpDataService.doPOST(
      url,
      undefined,
      distributor,
      undefined,
      'Error adding new distributor'
    );
  }

  public updateDistributor(profileId:number, oemId:number, distributor:IOEMDistributor | IOEMDetail, loadingMessage?:string):Promise<IOEMDistributor | IOEMDetail> {
    return this.httpDataService.doPUT(
      `rest/bpprofiles/profiles/${profileId}/oemrelations/${oemId}/distributorrelations/${distributor.id}`,
      undefined,
      distributor,
      loadingMessage,
      'Error updating distributor'
    );
  }

  public deleteDistributor(profileId:number, oemId:number, distributorId:number):Promise<void> {
    return this.httpDataService.doDELETE(
      `rest/bpprofiles/profiles/${profileId}/oemrelations/${oemId}/distributorrelations/${distributorId}`,
      undefined,
      undefined,
      'Error deleting distributor'
    );
  }

  public getPartnerCompanies(profileId:number, type?:string):Promise<ICompany[]> {
    return this.cache.doGET(
      `rest/bpprofiles/profiles/${profileId}/companies`,
      { type },
      undefined,
      'Error loading partner companies'
    );
  }

  public loadOFT(profileId:number, oemDetail:IOEMDetail):Promise<IOEMOrderFulfillmentTime|void> {
    const vendorId:number = oemDetail?.oem?.id;
    const companyCountryId:number = oemDetail?.companyCountry?.id;
    const processingModeId:string = oemDetail?.poPlacement?.id;
    const shippingProcessId:string = oemDetail?.shippingProcess?.id;

    if (vendorId && processingModeId && shippingProcessId) {
      let urlPrefix:string = `rest/bpprofiles/profiles/${profileId}/oft/${vendorId}/`;

      if (companyCountryId) {
        urlPrefix += `${companyCountryId}/`;
      }

      return this.httpDataService.doGET(
        `${urlPrefix + shippingProcessId}/${processingModeId}`,
        undefined,
        undefined,
        'Error loading OFT'
      );
    } else {
      return Promise.resolve();
    }
  }

  public listCLF(countryId:number, vendorId:number):Promise<ICLF[]> {
    return this.httpDataService.doGET(
      `rest/clf/${countryId}`,
      { vendorId },
      undefined,
      'Error loading CLF'
    );
  }

  public clear():void {
    this.cache.clear();
  }
}